import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { useControlledValue } from '../../molecules/FormField/useControlledValue';
import { SliderProps } from './Slider.types';

const usePresenter = (props: SliderProps) => {
  const {
    min = 0,
    max = 100,
    step = 1,
    value: propValue,
    onValueChange,
    showMin,
    showMax,
    showValueOnThumb,
    enableRange = false,
    disabled = false,
    invalid = false,
    required = false,
    className,
    ...otherSliderProps
  } = props;

  const { value: sliderValue, onValueChange: handleValueChange } = useControlledValue<number | [number, number]>({ value: propValue, onValueChange, defaultValue: min });

  const sliderRef = useRef<HTMLDivElement | null>(null);

  const value = useMemo(
    () => enableRange 
      ? (sliderValue as [number, number]) || [min, max]
      : (sliderValue as number) || min,
    [enableRange, sliderValue, min, max]
  );

  const [dragging, setDragging] = useState<number | null>(null);
  const [focusedThumb, setFocusedThumb] = useState<number | null>(null);

  const getPercentage = useCallback(
    (val: number) => ((val - min) / (max - min)) * 100,
    [min, max]
  );

  const getClosestThumb = useCallback((clientX: number) => {
    if (!enableRange || !sliderRef.current) return 0;
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const percent = ((clientX - left) / width) * 100;

    return Math.abs(getPercentage((value as [number, number])[0]) - percent) <
      Math.abs(getPercentage((value as [number, number])[1]) - percent)
      ? 0
      : 1;
  }, [enableRange, getPercentage, value]);

  const updateValueFromPosition = useCallback((clientX: number, index?: number) => {
    if (!sliderRef.current) return;
    const { left, width } = sliderRef.current.getBoundingClientRect();
    let percent = (clientX - left) / width;
    let newValue = Math.round((min + percent * (max - min)) / step) * step;
    newValue = Math.min(max, Math.max(min, newValue));

    handleValueChange((prev) => {
      if (enableRange) {
        const newValues = [...(prev as [number, number])] as [number, number];
        newValues[index as number] = newValue;
        newValues.sort((a, b) => a - b);
        return newValues;
      } else {
        return newValue;
      }
    });
  }, [min, max, step, enableRange, handleValueChange]);

  const startDrag = useCallback(
    (index?: number) => (e: React.MouseEvent | React.TouchEvent) => {
      if (!disabled) {
        e.stopPropagation();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const closestIndex = enableRange ? index ?? getClosestThumb(clientX) : 0;
        setDragging(closestIndex);
        setFocusedThumb(closestIndex);
        updateValueFromPosition(clientX, closestIndex);
      }
    },
    [disabled, enableRange, getClosestThumb, updateValueFromPosition]
  );

  useEffect(() => {
    const moveDrag = (e: MouseEvent | TouchEvent) => {
      if (dragging !== null) {
        updateValueFromPosition(
          'touches' in e ? e.touches[0].clientX : e.clientX,
          dragging
        );
      }
    };

    const stopDrag = () => {
      setDragging(null);
      setFocusedThumb(null);
    };

    window.addEventListener('mousemove', moveDrag);
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchmove', moveDrag);
    window.addEventListener('touchend', stopDrag);

    return () => {
      window.removeEventListener('mousemove', moveDrag);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', moveDrag);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [dragging, updateValueFromPosition]);

  const variantProps = useBuildVariantProps(
    'slider',
    {
    },
    {
      disabled,
    },
  );

  return {
    min,
    max,
    showMin,
    showMax,
    showValueOnThumb,
    enableRange,
    disabled,
    value,
    dragging,
    focusedThumb,
    sliderRef,
    getPercentage,
    startDrag,
    variantProps,
    otherSliderProps,
  };
};

export default usePresenter;